import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import { EnvVariable, Storage } from '../enums';
import { setAuthLocalStorage } from '../features/auth';
import { IAuth } from '../interfaces';
import { getStringEnvVariable } from '../utils';
import { OAuthApiUrls } from './utils';

export interface IBaseApiOptions {
  headers?: { [key: string]: string };
  withAuth?: boolean;
}

export class BaseApi {
  private client: AxiosInstance;

  constructor() {
    const headers = {
      'Content-Type': 'application/json',
      'Time-Zone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    const validateStatus = (status: number) => status >= 200 && status <= 299;

    const instance = axios.create({ headers, validateStatus });

    instance.interceptors.response.use(
      (response) => Promise.resolve(response.data),
      (error) => {
        const { response } = error;
        const { error_description } = response.data;

        if (response.status === 401 && error_description.includes('Access token expired')) {
          this.refreshToken().then((auth) => {
            setAuthLocalStorage(auth);
            window.location.reload();
          });
        }

        return Promise.reject(error);
      },
    );

    this.client = instance;
  }

  delete = <T>(url: URL, options: IBaseApiOptions = { headers: {}, withAuth: true }): Promise<T> => {
    return this.client.delete(url.toString(), this.getConfig(options));
  };

  get = <T>(url: URL, options: IBaseApiOptions = { headers: {}, withAuth: true }): Promise<T> => {
    return this.client.get(url.toString(), this.getConfig(options));
  };

  post = <T>(url: URL, data: unknown, options: IBaseApiOptions = { headers: {}, withAuth: true }): Promise<T> => {
    return this.client.post(url.toString(), data, this.getConfig(options));
  };

  patch = <T>(url: URL, data: unknown, options: IBaseApiOptions = { headers: {}, withAuth: true }): Promise<T> => {
    return this.client.patch(url.toString(), data, this.getConfig(options));
  };

  private refreshToken = (): Promise<IAuth> => {
    const refreshToken = localStorage.getItem(Storage.REFRESH_TOKEN);
    if (!refreshToken) {
      return Promise.reject('Missing refresh token');
    }

    return this.client.post(
      OAuthApiUrls.refreshToken().toString(),
      {
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      },
      this.getConfig({ headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, withAuth: false }),
    );
  };

  private getConfig = (options: IBaseApiOptions): AxiosRequestConfig => {
    const { headers, withAuth } = options;
    const accessToken = localStorage.getItem(Storage.ACCESS_TOKEN) ?? '';
    const authToken = getStringEnvVariable(EnvVariable.AUTHORIZATION_TOKEN);

    return {
      headers: {
        ...(headers || {}),
        Authorization: withAuth ? `Bearer ${accessToken}` : `Basic ${authToken}`,
      },
    };
  };
}
